import { Add as AddIcon, Clear as ClearIcon } from '@mui/icons-material';
import { Box, Paper, Stack, Table, TableBody, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { parse } from 'papaparse';
import { useEffect, useRef } from 'react';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { toast } from 'react-toastify';
import { useAccount } from 'wagmi';

import { Show } from 'src/components/Show';
import { colors } from 'src/global/styles/colors';
import { ProjectInputs } from 'src/global/types/project.type';
import { useDeployment } from 'src/provider/DeploymentProvider';
import { useGetRecipientsQuery } from 'src/store/services/project.service';
import { parseConfig, validateCsv } from 'src/utils/template';

import { AdminInput } from '../AdminTextField';
import { DownloadButton } from '../DownloadButton';
import { UploadButton } from '../UploadButton';
import { CustomTableBodyCell, CustomTableHeadCell } from './CustomTableCell';

export function FundingRecipients() {
  const { hasDeployed } = useDeployment();
  const { address } = useAccount();
  const {
    control,
    formState: { errors, isSubmitted, touchedFields },
    watch,
    getValues,
    trigger,
  } = useFormContext<ProjectInputs>();
  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: 'recipients',
  });

  const { data: defaultRecipients = [], isLoading: isLoadingRecipients } = useGetRecipientsQuery(
    { address: address as string },
    { skip: !address },
  );

  const recipients = watch('recipients') || [];

  const shouldShowErrors = isSubmitted || touchedFields.recipients;

  const initializationDoneRef = useRef(false);

  useEffect(() => {
    if (isLoadingRecipients || hasDeployed || !address || defaultRecipients.length === 0) return;
    if (initializationDoneRef.current) return;

    const currentRecipients = getValues('recipients') || [];
    const hasDefaultRecipients = currentRecipients.some(r => r?.isDefault);

    if (hasDefaultRecipients) {
      initializationDoneRef.current = true;
      return;
    }

    const hasOnlyEmptyPlaceholder =
      currentRecipients.length === 1 &&
      currentRecipients[0] &&
      (!currentRecipients[0].name || !currentRecipients[0].address);

    if (hasOnlyEmptyPlaceholder || currentRecipients.length === 0) {
      const defaultRecipientsList = defaultRecipients.map(recipient => ({
        name: recipient.name,
        address: recipient.address,
        isDefault: true,
        minPercent: undefined,
        maxPercent: undefined,
      }));

      const allRecipients = [
        ...defaultRecipientsList,
        { name: '', address: '', isDefault: false, minPercent: undefined, maxPercent: undefined },
      ];
      replace(allRecipients);
      initializationDoneRef.current = true;
    }
  }, [defaultRecipients, isLoadingRecipients, address, hasDeployed, replace, getValues]);

  const handleUploadCsvFile = (files: FileList) => {
    const file = files[0];
    const reader = new FileReader();

    reader.onload = async e => {
      try {
        if (!e.target?.result) {
          toast.error('Error reading file');
          return;
        }

        const csv = e.target.result as string;
        const validationRes = validateCsv(csv, {
          headers: ['NAME', 'WALLET_ADDRESS'],
          columnTypes: ['string', 'address'],
        });

        if (!validationRes.isValid) {
          toast.error(validationRes.errors[0]);
          return;
        }

        const parseResult = parse(csv, parseConfig);
        const csvRecipients = parseResult.data
          .slice(1)
          .filter((row: any) => row.length >= 2 && row[0]?.trim() && row[1]?.trim())
          .map((row: any) => ({
            name: row[0].trim(),
            address: row[1].trim(),
            isDefault: false,
            minPercent: undefined,
            maxPercent: undefined,
          }));

        if (csvRecipients.length === 0) {
          toast.error('No valid recipients found in CSV');
          return;
        }

        const currentRecipients = [...fields];

        const updatedRecipients = [...currentRecipients];

        csvRecipients.forEach(csvRecipient => {
          const existingIndex = currentRecipients.findIndex(
            existing => existing.name.toLowerCase() === csvRecipient.name.toLowerCase(),
          );

          if (existingIndex >= 0) {
            const existing = updatedRecipients[existingIndex];
            if (!existing.isDefault) {
              updatedRecipients[existingIndex] = {
                ...existing,
                address: csvRecipient.address,
              };
            }
          } else {
            // This is a new recipient - we'll use append() to add it later
            // Don't add it to updatedRecipients here
          }
        });

        replace(updatedRecipients);

        const newRecipients = csvRecipients.filter(
          csvRecipient =>
            !currentRecipients.some(
              existing => existing.address.toLowerCase() === csvRecipient.address.toLowerCase() || existing.isDefault,
            ),
        );

        for (const newRecipient of newRecipients) {
          append(newRecipient);
        }

        toast.success(`Recipients uploaded successfully`);
      } catch (error) {
        console.error('Error processing CSV:', error);
        toast.error('Error processing CSV file');
      }
    };

    reader.onerror = () => {
      toast.error('Error reading the CSV file');
    };

    reader.readAsText(file);
  };

  return (
    <Box width={'100%'} height={'100%'} display={'flex'} flexDirection={'column'}>
      <Show when={!hasDeployed}>
        <Stack
          spacing={2}
          gap={2}
          flexDirection={{ xs: 'column', sm: 'row' }}
          style={{
            width: 'fit-content',
          }}>
          <UploadButton accept='.csv' label='Import CSV File' multiple={false} uploadCallback={handleUploadCsvFile} />
          <DownloadButton label='Download Template' fileUrl='/resources/files/templates/recipient-template.csv' />
        </Stack>
      </Show>
      <Typography variant='h1' fontWeight={500} fontSize={'16px'} color='white' paddingY={3}>
        Funding Recipients
      </Typography>

      <TableContainer
        component={Paper}
        sx={{ minWidth: 650, backgroundColor: 'transparent !important', color: colors.neutral90 }}>
        <Table
          sx={{ minWidth: 650, backgroundColor: colors.neutral06, color: colors.neutral90 }}
          size='small'
          aria-label='a dense table'>
          <TableHead sx={{ minWidth: 650, backgroundColor: colors.neutral06, color: colors.neutral90, paddingY: 1 }}>
            <TableRow>
              <CustomTableHeadCell width={'40%'}>Name</CustomTableHeadCell>
              <CustomTableHeadCell width={'60%'}>Wallet Address</CustomTableHeadCell>
            </TableRow>
          </TableHead>
          <TableBody
            sx={{
              background: 'transparent',
              color: 'white',
            }}>
            {fields.map((field, index) => {
              const isDefault = Boolean(defaultRecipients?.find(item => item.address === field.address));

              return (
                <TableRow
                  key={field.id}
                  sx={{
                    paddingY: 1,
                    borderBottom: `1px solid ${index === fields.length - 1 ? 'none' : colors.neutral015}`,
                    '&:last-child td, &:last-child th': { border: 0 },
                  }}>
                  <CustomTableBodyCell>
                    <Controller
                      name={`recipients.${index}.name` as any}
                      control={control}
                      render={({ field }) => {
                        // Only show errors if field is touched or form is submitted
                        const isFieldTouched = touchedFields.recipients?.[index]?.name;
                        const shouldShowFieldError =
                          (isSubmitted || isFieldTouched) && !!errors?.recipients?.[index]?.name;

                        return (
                          <AdminInput
                            disabled={isDefault || hasDeployed}
                            isError={shouldShowFieldError}
                            errorMsg={shouldShowFieldError ? errors?.recipients?.[index]?.name?.message : undefined}
                            placeholder={'Enter name'}
                            focusedBorderColor='white'
                            borderColor={shouldShowFieldError ? colors.error : !field.value ? 'white' : 'transparent'}
                            {...field}
                            onBlur={() => {
                              field.onBlur();
                              // Trigger validation for this field when user leaves it
                              if (!isDefault) {
                                trigger(`recipients.${index}.name`);
                              }
                            }}
                          />
                        );
                      }}
                    />
                  </CustomTableBodyCell>
                  <CustomTableBodyCell>
                    <div className='flex justify-between items-start'>
                      <Box width={'-webkit-fill-available'}>
                        <Controller
                          name={`recipients.${index}.address` as any}
                          control={control}
                          render={({ field }) => {
                            // Only show errors if field is touched or form is submitted
                            const isFieldTouched = touchedFields.recipients?.[index]?.address;
                            const shouldShowFieldError =
                              (isSubmitted || isFieldTouched) && !!errors?.recipients?.[index]?.address;

                            return (
                              <AdminInput
                                disabled={isDefault || hasDeployed}
                                isError={shouldShowFieldError}
                                errorMsg={
                                  shouldShowFieldError ? errors?.recipients?.[index]?.address?.message : undefined
                                }
                                placeholder={'Enter Wallet Address'}
                                focusedBorderColor='white'
                                borderColor={
                                  shouldShowFieldError ? colors.error : !field.value ? 'white' : 'transparent'
                                }
                                style={{
                                  width: '-webkit-fill-available',
                                }}
                                {...field}
                                onBlur={() => {
                                  field.onBlur();
                                  // Trigger validation for this field when user leaves it
                                  if (!isDefault) {
                                    trigger(`recipients.${index}.address`);
                                  }
                                }}
                              />
                            );
                          }}
                        />
                      </Box>
                      <Show when={!hasDeployed}>
                        <Stack direction='row' spacing={1} alignItems={'center'} marginLeft={'10px'} marginTop={'12px'}>
                          {index === fields.length - 1 && (
                            <AddIcon
                              onClick={() => {
                                append({ name: '', address: '', isDefault: false });
                              }}
                              className='cursor-pointer'
                              sx={{ color: 'white' }}
                            />
                          )}
                          {!isDefault && index < fields.length - 1 && (
                            <ClearIcon
                              onClick={() => remove(index)}
                              className='cursor-pointer'
                              sx={{
                                color: colors.error,
                              }}
                            />
                          )}
                        </Stack>
                      </Show>
                    </div>
                  </CustomTableBodyCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <Box marginTop={2}>
        {shouldShowErrors && errors?.recipients?.message && (
          <Typography variant='body2' fontSize={'14px'} color={colors.error} paddingBottom={2}>
            {errors.recipients.message}
          </Typography>
        )}
      </Box>
    </Box>
  );
}
